import React, { useState, useEffect } from 'react';
import { Calculator } from 'lucide-react';
import { SEO } from '../components/SEO';

interface LeasingState {
  vehiclePrice: string;
  downPayment: string;
  leaseTerm: string;
  interestRate: string;
  residualValue: string;
  currency: 'EUR' | 'BAM' | 'RSD';
}

const currencySymbols: Record<string, string> = {
  EUR: '€',
  BAM: 'KM',
  RSD: 'RSD'
};

function LeasingKalkulator() {
  const [values, setValues] = useState<LeasingState>({
    vehiclePrice: '',
    downPayment: '',
    leaseTerm: '48',
    interestRate: '5.99',
    residualValue: '20',
    currency: 'EUR'
  });

  const [results, setResults] = useState<{
    monthlyPayment: number;
    totalInterest: number;
    totalCost: number;
    finalResidual: number;
  } | null>(null);

  const calculateLeasing = () => {
    const price = parseFloat(values.vehiclePrice);
    const down = parseFloat(values.downPayment);
    const term = parseFloat(values.leaseTerm);
    const rate = parseFloat(values.interestRate) / 100 / 12; // Monthly interest rate
    const residual = (parseFloat(values.residualValue) / 100) * price;

    if (isNaN(price) || isNaN(down) || isNaN(term) || isNaN(rate) || isNaN(residual)) {
      return null;
    }

    const loanAmount = price - down;
    const monthlyPayment = (
      (loanAmount - residual) +
      ((loanAmount + residual) * (rate * term))
    ) / term;

    const totalCost = (monthlyPayment * term) + down + residual;
    const totalInterest = totalCost - price;

    return {
      monthlyPayment: parseFloat(monthlyPayment.toFixed(2)),
      totalInterest: parseFloat(totalInterest.toFixed(2)),
      totalCost: parseFloat(totalCost.toFixed(2)),
      finalResidual: parseFloat(residual.toFixed(2))
    };
  };

  const handleInputChange = (field: keyof LeasingState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    if (values.vehiclePrice && values.downPayment) {
      const results = calculateLeasing();
      setResults(results);
    }
  }, [values]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('hr-HR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Leasing Kalkulator",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte mjesečne rate i ukupne troškove leasinga. Precizni izračuni za različite vrste leasinga i vozila.",
    "url": "https://online-kalkulator.com/leasing-kalkulator",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Izračun rate leasinga",
      "Izračun ukupnih troškova",
      "Viševalutna podrška",
      "Automatsko ažuriranje",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Leasing Kalkulator | Izračun Rate Leasinga"
        description="Izračunajte mjesečnu ratu leasinga, ukupne troškove i ostatak vrijednosti. Precizni izračuni za različite vrste leasinga i vozila."
        canonicalUrl="/leasing-kalkulator"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Calculator className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Leasing Kalkulator</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vrijednost vozila
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={values.vehiclePrice}
                      onChange={(e) => handleInputChange('vehiclePrice', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={`Unesite iznos u ${values.currency}`}
                    />
                    <span className="absolute right-3 top-2 text-gray-500">
                      {currencySymbols[values.currency]}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Učešće
                  </label>
                  <input
                    type="number"
                    value={values.downPayment}
                    onChange={(e) => handleInputChange('downPayment', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Iznos učešća"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valuta
                  </label>
                  <select
                    value={values.currency}
                    onChange={(e) => handleInputChange('currency', e.target.value as LeasingState['currency'])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="EUR">Euro (€)</option>
                    <option value="BAM">Konvertibilna marka (KM)</option>
                    <option value="RSD">Srpski dinar (RSD)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trajanje leasinga (mjeseci)
                  </label>
                  <select
                    value={values.leaseTerm}
                    onChange={(e) => handleInputChange('leaseTerm', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="24">24 mjeseca</option>
                    <option value="36">36 mjeseci</option>
                    <option value="48">48 mjeseci</option>
                    <option value="60">60 mjeseci</option>
                    <option value="72">72 mjeseca</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kamatna stopa (%)
                  </label>
                  <input
                    type="number"
                    value={values.interestRate}
                    onChange={(e) => handleInputChange('interestRate', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 5.99"
                    step="0.01"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ostatak vrijednosti (%)
                  </label>
                  <input
                    type="number"
                    value={values.residualValue}
                    onChange={(e) => handleInputChange('residualValue', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 20"
                    step="1"
                  />
                </div>
              </div>

              {/* Results */}
              {results && (
                <div className="space-y-4 border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Mjesečna rata:</span>
                    <span className="text-xl font-bold text-blue-600">
                      {formatCurrency(results.monthlyPayment)} {currencySymbols[values.currency]}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Ukupna kamata:</span>
                    <span className="font-semibold">
                      {formatCurrency(results.totalInterest)} {currencySymbols[values.currency]}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Ostatak vrijednosti:</span>
                    <span className="font-semibold">
                      {formatCurrency(results.finalResidual)} {currencySymbols[values.currency]}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-3 border-t">
                    <span className="text-gray-800 font-medium">Ukupni trošak:</span>
                    <span className="text-xl font-bold text-blue-600">
                      {formatCurrency(results.totalCost)} {currencySymbols[values.currency]}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Leasing Kalkulatoru</h2>
                <p className="text-gray-600 leading-relaxed">
                  Leasing kalkulator vam pomaže izračunati mjesečne rate i ukupne troškove leasinga za
                  vozilo ili opremu. Uzima u obzir sve važne faktore kao što su vrijednost vozila,
                  učešće, kamatna stopa i ostatak vrijednosti na kraju leasinga.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Vrste Leasinga</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Financijski leasing</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Vozilo prelazi u vlasništvo nakon otplate</li>
                      <li>Veće mjesečne rate, manji ostatak vrijednosti</li>
                      <li>Mogućnost otplate prije roka</li>
                      <li>Troškovi održavanja na strani korisnika</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Operativni leasing</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Vozilo se vraća nakon isteka ugovora</li>
                      <li>Niže mjesečne rate, veći ostatak vrijednosti</li>
                      <li>Često uključuje održavanje i osiguranje</li>
                      <li>Ograničena kilometraža</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite vrijednost vozila ili opreme</li>
                  <li>Odredite iznos učešća (obično 10-30%)</li>
                  <li>Izaberite trajanje leasinga</li>
                  <li>Unesite kamatnu stopu</li>
                  <li>Postavite ostatak vrijednosti (za operativni leasing)</li>
                  <li>Rezultati će se automatski izračunati</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važni Pojmovi</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Učešće: Početni iznos koji plaćate odmah</li>
                  <li>Kamatna stopa: Godišnja kamatna stopa na leasing</li>
                  <li>Ostatak vrijednosti: Procijenjeni iznos nakon isteka leasinga</li>
                  <li>Mjesečna rata: Redovita mjesečna uplata</li>
                  <li>Ukupni trošak: Zbroj svih troškova tijekom trajanja leasinga</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Dodatni Troškovi</h2>
                <p className="text-gray-600 mb-4">
                  Osim mjesečnih rata, trebate računati i na dodatne troškove:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Troškovi obrade zahtjeva</li>
                  <li>Obvezno osiguranje</li>
                  <li>Kasko osiguranje (često obavezno)</li>
                  <li>Troškovi održavanja</li>
                  <li>Registracija vozila</li>
                  <li>Eventualni troškovi prijevremenog raskida</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Savjeti za Leasing</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Usporedite ponude različitih leasing kuća</li>
                  <li>Provjerite sve uvjete ugovora prije potpisivanja</li>
                  <li>Razmislite o veličini učešća - veće učešće znači manje rate</li>
                  <li>Pazite na ograničenja kilometraže kod operativnog leasinga</li>
                  <li>Provjerite uvjete prijevremenog raskida ugovora</li>
                  <li>Uzmite u obzir sve dodatne troškove</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default LeasingKalkulator;